import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	AppLayout,
	Box,
	Link,
	SpaceBetween,
	Spinner,
	SplitPanel,
	TextFilter,
} from '@amzn/awsui-components-console';
import { useStore } from 'src/stores/store';
import { Store } from 'src/stores/store.interface';
import { SnowAttribute } from '@amzn/snow-nxt-workflow-service-typescript-client';
import { TaskDefinition } from 'src/stores/workflowDefinitionCanvasStore/workflowDefinitionCanvasStore.interface';
import { callAutoPopulationService } from 'src/utils/autoPopulationService';
import { useAuthContext } from 'src/providers/AuthContext';
import {
	AttributeRow,
	AutoPopulationDefinition,
	MappingEntry,
	ValidationDefinition,
	entriesToRecord,
	recordToEntries,
} from './configureAttributeStep.types';
import {
	AutoPopulationOperation,
	AutoPopulationType,
} from '@amzn/snow-auto-population-service-typescript-client';
import AttributeTable from './AttributeTable';
import AutoPopulationPanel from './AutoPopulationPanel';
import TaskValidationsSection from './TaskValidationsSection';
import {
	clearConfigureAttributeSaver,
	registerConfigureAttributeSaver,
} from './configureAttributeSaver';
import {
	ValidationMetadataSummary,
	listValidationMetadata,
} from './validationMetadataApi';
import {
	isDerivedAttribute,
	isGlobalAttribute,
	isWorkflowSpecificAttribute,
} from 'src/utils/attributeTypeUtils';

export default function ConfigureAttributeStep() {
	const WFDefinition = useStore((state: Store) => state.WFDefinition);
	const { taskDefinitions } = WFDefinition;
	const setWFDefinition = useStore((state: Store) => state.setWFDefinition);
	const workflowCreationMetaData = useStore(
		(state: Store) => state.workflowCreationMetaData
	);
	const { workflowId } = workflowCreationMetaData;
	const authContext = useAuthContext();

	const [definitions, setDefinitions] = useState<Record<string, AutoPopulationDefinition>>({});
	const [operations, setOperations] = useState<AutoPopulationOperation[]>([]);
	const [validationMetadataSummaries, setValidationMetadataSummaries] = useState<
		ValidationMetadataSummary[]
	>([]);
	const [selectedAttribute, setSelectedAttribute] = useState<AttributeRow | null>(null);
	const [selectedTaskName, setSelectedTaskName] = useState<string | null>(null);
	const [editingDef, setEditingDef] = useState<AutoPopulationDefinition | null>(null);
	const [filterText, setFilterText] = useState('');
	const [isLoadingDefinitions, setIsLoadingDefinitions] = useState(true);
	const [isLoadingOperations, setIsLoadingOperations] = useState(true);
	const isLoading = isLoadingDefinitions || isLoadingOperations;
	const operationOptions = useMemo(() =>
		operations
			.filter((op) => !!op.operationId)
			.map((op) => ({
				label: op.autoPopulationId ?? op.operationId!,
				value: op.operationId!,
			})),
	[operations]);

	const operationsByOperationId = useMemo(() => {
		const map: Record<string, AutoPopulationOperation> = {};

		operations.forEach((op) => {
			if (op.operationId) map[op.operationId] = op;
		});

		return map;
	}, [operations]);
	const [requestMappingItems, setRequestMappingItems] = useState<MappingEntry[]>([]);
	const [responseMappingItems, setResponseMappingItems] = useState<MappingEntry[]>([]);
	const [customOrder, setCustomOrder] = useState<Record<string, string[]>>({});
	const [splitPanelPosition, setSplitPanelPosition] = useState<'side' | 'bottom'>('side');

	const updateSectionOrder = (sectionKey: string, order: string[]) =>
		setCustomOrder((prev) => ({ ...prev, [sectionKey]: order }));

	const workflowDefinitionName =
		workflowCreationMetaData.workflowDefinitionName || 'this workflow';

	const populatedByOperationLabel = useMemo(() => {
		const map: Record<string, string> = {};

		Object.values(definitions).forEach((def) => {
			if (!def?.operationId || !def.responseMapping) return;
			const sourceLabel =
				operationOptions.find((o) => o.value === def.operationId)?.label ||
				def.autoPopulationId ||
				def.operationId;

			Object.keys(def.responseMapping).forEach((targetAttrId) => {
				if (!(targetAttrId in map)) map[targetAttrId] = sourceLabel;
			});
		});

		return map;
	}, [definitions, operationOptions]);

	const { workflowAttributes, taskAttributeGroups } = useMemo(() => {
		const commonMap = new Map<string, AttributeRow>();
		const taskGroups: { taskName: string; attributes: AttributeRow[] }[] = [];

		taskDefinitions.forEach((taskDef: TaskDefinition) => {
			const taskSpecificAttributes: AttributeRow[] = [];

			(taskDef.taskInputs || []).forEach((attr: SnowAttribute) => {
				if (isGlobalAttribute(attr) || isDerivedAttribute(attr)) {
					return;
				}

				const id = attr.attributeId ?? '';
				const def = definitions[id];
				const opName = def?.operationId
					? operationOptions.find((o) => o.value === def.operationId)?.label || def.autoPopulationId || 'None'
					: populatedByOperationLabel[id] || def?.autoPopulationId || 'None';
				const row: AttributeRow = {
					attributeId: id,
					attributeName: attr.attributeName ?? '',
					dataType: attr.dataType || 'String',
					autoPopulationId: opName,
					validationId: 'None',
					taskName: taskDef.taskName,
				};

				taskSpecificAttributes.push(row);

				const isCommon = isWorkflowSpecificAttribute(attr);

				if (isCommon && !commonMap.has(id)) {
					commonMap.set(id, { ...row, taskName: 'Common' });
				}
			});
			if (taskSpecificAttributes.length > 0) {
				taskGroups.push({
					taskName: taskDef.taskName,
					attributes: taskSpecificAttributes,
				});
			}
		});

		return {
			workflowAttributes: [...commonMap.values()],
			taskAttributeGroups: taskGroups,
		};
	}, [taskDefinitions, definitions, operationOptions, populatedByOperationLabel]);

	const allAttributeIds = useMemo(() => {
		const ids = new Set<string>();

		workflowAttributes.forEach((a) => ids.add(a.attributeId));
		taskAttributeGroups.forEach((g) =>
			g.attributes.forEach((a) => ids.add(a.attributeId))
		);

		return [...ids].map((id) => ({ label: id, value: id }));
	}, [workflowAttributes, taskAttributeGroups]);

	useEffect(() => {
		const fetchDefinitions = async () => {
			setIsLoadingDefinitions(true);
			try {
				const response = await callAutoPopulationService({
					path: `/autoPopulation/definitions/${workflowId}`,
					method: 'GET',
				});

				setDefinitions(response?.definition || {});
			} catch (error) {
				console.error('Failed to fetch auto-population definitions:', error);
			} finally {
				setIsLoadingDefinitions(false);
			}
		};

		if (workflowId) {
			fetchDefinitions();
		} else {
			setIsLoadingDefinitions(false);
		}
	}, [workflowId]);

	useEffect(() => {
		const fetchOperations = async () => {
			setIsLoadingOperations(true);
			try {
				const response = await callAutoPopulationService({
					path: '/autoPopulation/operations',
					method: 'GET',
				});

				setOperations(response?.operations || []);
			} catch (error) {
				console.error('Failed to fetch auto-population operations:', error);
			} finally {
				setIsLoadingOperations(false);
			}
		};

		fetchOperations();
	}, []);

	useEffect(() => {
		const fetchValidationMetadata = async () => {
			try {
				const summaries = await listValidationMetadata();

				setValidationMetadataSummaries(summaries.filter((s) => !!s.validationId));
			} catch (error) {
				console.error('Failed to fetch validation metadata:', error);
				setValidationMetadataSummaries([]);
			}
		};

		fetchValidationMetadata();
	}, []);

	const updateTaskValidations = (
		taskName: string,
		nextValidations: ValidationDefinition[]
	) => {
		const nextTaskDefinitions = taskDefinitions.map((taskDef: TaskDefinition) =>
			taskDef.taskName === taskName
				? { ...taskDef, validationDefinitions: nextValidations }
				: taskDef
		);

		setWFDefinition({ ...WFDefinition, taskDefinitions: nextTaskDefinitions });
	};

	const validationsByTaskName = useMemo(() => {
		const map: Record<string, ValidationDefinition[]> = {};

		taskDefinitions.forEach((taskDef: TaskDefinition) => {
			map[taskDef.taskName] = taskDef.validationDefinitions ?? [];
		});

		return map;
	}, [taskDefinitions]);

	const taskInputIdsByTaskName = useMemo(() => {
		const map: Record<string, string[]> = {};

		taskDefinitions.forEach((taskDef: TaskDefinition) => {
			map[taskDef.taskName] = (taskDef.taskInputs || [])
				.map((attr: SnowAttribute) => attr.attributeId ?? '')
				.filter(Boolean);
		});

		return map;
	}, [taskDefinitions]);

	const isAutoPopulationDefinitionModified = (def: AutoPopulationDefinition): boolean => {
		if (def.autoPopulationType && def.autoPopulationType !== 'NOOP') return true;
		if (def.dependencies?.length) return true;

		return !!def.dependents?.length;
	};

	const handleSelectAttribute = (row: AttributeRow) => {
		if (editingDef && selectedAttribute) {
			if (isAutoPopulationDefinitionModified(editingDef) || definitions[selectedAttribute.attributeId]) {
				setDefinitions((prev) => ({
					...prev,
					[selectedAttribute.attributeId]: editingDef,
				}));
			}
		}

		setSelectedTaskName(null);
		setSelectedAttribute(row);
		const existing = definitions[row.attributeId];
		const def = existing || {
			autoPopulationType: 'NOOP',
			dependencies: [],
			dependents: [],
		};

		setEditingDef({ ...def, autoPopulationId: row.attributeId });
		setRequestMappingItems(recordToEntries(def.requestMapping));
		const responseAsStrings = Object.fromEntries(
			Object.entries(def.responseMapping || {}).map(([k, v]) => [k, v?.sourceAttribute ?? ''])
		);

		setResponseMappingItems(recordToEntries(responseAsStrings));
	};

	const handleSelectTask = (taskName: string) => {
		if (editingDef && selectedAttribute) {
			if (isAutoPopulationDefinitionModified(editingDef) || definitions[selectedAttribute.attributeId]) {
				setDefinitions((prev) => ({
					...prev,
					[selectedAttribute.attributeId]: editingDef,
				}));
			}
		}

		setSelectedAttribute(null);
		setEditingDef(null);
		setSelectedTaskName(taskName);
	};

	const updateRequestMapping = (items: MappingEntry[]) => {
		setRequestMappingItems(items);
		setEditingDef((prev) => (prev ? { ...prev, requestMapping: entriesToRecord(items) } : prev));
	};

	const updateResponseMapping = (items: MappingEntry[]) => {
		setResponseMappingItems(items);
		const responseRecord = Object.fromEntries(
			Object.entries(entriesToRecord(items)).map(([k, v]) => [k, { sourceAttribute: v }])
		);

		setEditingDef((prev) => (prev ? { ...prev, responseMapping: responseRecord } : prev));
	};

	const handleOperationChange = (operationId: string) => {
		const op = operationsByOperationId[operationId];

		setEditingDef((prev) => {
			if (!prev) return prev;
			const responseRecord = Object.fromEntries(
				Object.entries(op?.responseMapping || {}).map(([k, v]) => [k, { sourceAttribute: v }])
			);

			return {
				...prev,
				operationId,
				isOverridable: op?.isOverridable ?? prev.isOverridable,
				requestMapping: { ...(op?.requestMapping || {}) },
				responseMapping: responseRecord,
			};
		});
		setRequestMappingItems(recordToEntries(op?.requestMapping));
		setResponseMappingItems(recordToEntries(op?.responseMapping));
	};

	const validateDefinitions = (defs: Record<string, AutoPopulationDefinition>): string[] => {
		const errors: string[] = [];

		Object.entries(defs).forEach(([attrId, def]) => {
			if (def.autoPopulationType === AutoPopulationType.EXTERNAL && !def.operationId) {
				errors.push(`${attrId}: EXTERNAL type requires an AutoPopulation Operation`);
			}

			if (def.autoPopulationType === AutoPopulationType.STATIC) {
				if (!(def as any).staticValueConfig?.value) {
					errors.push(`${attrId}: STATIC type requires a Value`);
				}
			}
		});

		return errors;
	};

	const saverStateRef = useRef({
		definitions,
		editingDef,
		selectedAttribute,
		workflowId,
		userName: authContext.userName,
	});

	useEffect(() => {
		saverStateRef.current = {
			definitions,
			editingDef,
			selectedAttribute,
			workflowId,
			userName: authContext.userName,
		};
	}, [definitions, editingDef, selectedAttribute, workflowId, authContext.userName]);

	useEffect(() => {
		registerConfigureAttributeSaver({
			validate: () => {
				const state = saverStateRef.current;
				const completeDefinition: Record<string, AutoPopulationDefinition> = {
					...state.definitions,
					...(state.editingDef && state.selectedAttribute
						? { [state.selectedAttribute.attributeId]: state.editingDef }
						: {}),
				};

				return validateDefinitions(completeDefinition);
			},
			save: async () => {
				const state = saverStateRef.current;
				const completeDefinition: Record<string, AutoPopulationDefinition> = {
					...state.definitions,
					...(state.editingDef && state.selectedAttribute
						? { [state.selectedAttribute.attributeId]: state.editingDef }
						: {}),
				};

				try {
					await callAutoPopulationService({
						path: '/autoPopulation/definitions',
						method: 'POST',
						body: {
							workflowId: state.workflowId,
							definition: completeDefinition,
						},
					});
					setDefinitions(completeDefinition);
				} catch (error) {
					throw new Error(
						`Failed to save AutoPopulation definitions: ${
							(error as Error)?.message ?? String(error)
						}`
					);
				}
			},
		});

		return () => clearConfigureAttributeSaver();
	}, []);

	return (
		<>
			<AppLayout
				contentType="default"
				navigationHide
				toolsHide
				splitPanel={
					<SplitPanel
						header={
							selectedTaskName
								? `Validations for ${selectedTaskName}`
								: selectedAttribute
									? `Configure: ${selectedAttribute.attributeName}`
									: 'Attribute Configuration'
						}
					>
						{selectedTaskName && (
							<TaskValidationsSection
								taskName={selectedTaskName}
								validations={validationsByTaskName[selectedTaskName] ?? []}
								operationOptions={operationOptions}
								onUpdate={(next) => updateTaskValidations(selectedTaskName, next)}
								taskInputAttributeIds={taskInputIdsByTaskName[selectedTaskName]}
								validationMetadataSummaries={validationMetadataSummaries}
							/>
						)}
						{!selectedTaskName && editingDef && (
							<AutoPopulationPanel
								editingDef={editingDef}
								setEditingDef={setEditingDef}
								operationOptions={operationOptions}
								onOperationChange={handleOperationChange}
								allAttributeIds={allAttributeIds}
								mapping={{
									requestItems: requestMappingItems,
									responseItems: responseMappingItems,
									onUpdateRequest: updateRequestMapping,
									onUpdateResponse: updateResponseMapping,
									requestBodyKeys: Object.keys(
										operationsByOperationId[editingDef.operationId ?? '']?.requestMapping ?? {}
									),
								}}
							/>
						)}
					</SplitPanel>
				}
				splitPanelOpen={!!selectedAttribute || !!selectedTaskName}
				splitPanelPreferences={{ position: splitPanelPosition }}
				onSplitPanelPreferencesChange={({ detail }) => {
					setSplitPanelPosition(detail.position);
				}}
				onSplitPanelToggle={({ detail }) => {
					if (!detail.open) {
						if (editingDef && selectedAttribute && isAutoPopulationDefinitionModified(editingDef)) {
							setDefinitions((prev) => ({
								...prev,
								[selectedAttribute.attributeId]: editingDef,
							}));
						}

						setSelectedAttribute(null);
						setSelectedTaskName(null);
					}
				}}
				content={
					isLoading ? (
						<Box textAlign="center" padding="xxl">
							<Spinner size="large" />
							<Box variant="p" padding={{ top: 's' }}>
								Loading Attribute configuration...
							</Box>
						</Box>
					) : (
						<SpaceBetween size="l">
							<Box>
								Configure AutoPopulation/Validation definitions for attributes across your
								workflow tasks.
								Select an attribute to configure AutoPopulation.
								Click on Task Header to configure Validation
							</Box>
							<TextFilter
								filteringText={filterText}
								filteringPlaceholder="Find attributes by name or ID"
								onChange={({ detail }) => setFilterText(detail.filteringText)}
							/>
							{(() => {
								const matchesFilter = (a: AttributeRow) => {
									if (!filterText) return true;
									const q = filterText.toLowerCase();

									return (
										a.attributeName.toLowerCase().includes(q) ||
										a.attributeId.toLowerCase().includes(q)
									);
								};

								const workflowVisible = workflowAttributes.some(matchesFilter);
								const visibleTaskGroups = taskAttributeGroups.filter((g) =>
									g.attributes.some(matchesFilter)
								);

								if (!workflowVisible && visibleTaskGroups.length === 0) {
									return (
										<Box textAlign="center" padding="m">
											No attributes match the filter
										</Box>
									);
								}

								return (
									<>
										{workflowVisible && (
											<>
												<Box variant="h3" padding={{ bottom: 'xxs' }}>
													{`Workflow Inputs for ${workflowDefinitionName}`}
												</Box>
												<AttributeTable
													items={workflowAttributes}
													definitions={definitions}
													filterText={filterText}
													selectedAttribute={selectedAttribute}
													onSelectAttribute={handleSelectAttribute}
													customOrder={customOrder.workflow ?? null}
													onReorder={(order) => updateSectionOrder('workflow', order)}
												/>
											</>
										)}
										{visibleTaskGroups.map((group) => {
											const validationCount =
												(validationsByTaskName[group.taskName] ?? []).length;
											const isTaskSelected = selectedTaskName === group.taskName;

											return (
												<React.Fragment key={group.taskName}>
													<Box padding={{ top: 's', bottom: 'xxs' }}>
														<Link
															onFollow={(e) => {
																e.preventDefault();
																handleSelectTask(group.taskName);
															}}
															variant="primary"
															fontSize="heading-m"
														>
															{`Task: ${group.taskName}${
																validationCount
																	? ` (${validationCount} validation${
																		validationCount === 1 ? '' : 's'
																	})`
																	: ''
															}${isTaskSelected ? ' — editing validations' : ''}`}
														</Link>
													</Box>
													<AttributeTable
														items={group.attributes}
														definitions={definitions}
														filterText={filterText}
														selectedAttribute={selectedAttribute}
														onSelectAttribute={handleSelectAttribute}
														customOrder={customOrder[`task:${group.taskName}`] ?? null}
														onReorder={(order) =>
															updateSectionOrder(`task:${group.taskName}`, order)
														}
													/>
												</React.Fragment>
											);
										})}
									</>
								);
							})()}
						</SpaceBetween>
					)
				}
			/>
		</>
	);
}
